import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Button, Card, Chip, EmptyState, Tabs } from '@heroui/react'
import { Icon, type IconName } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { awayStage } from '../lib/cashier'
import { bookedMark, callableFrom } from '../lib/bookings'
import { backInMinutes, isHeld, parkedSeconds } from '../lib/branchState'
import { callingWaitSeconds } from '../lib/callingWait'
import { offersOutOfTurnCall } from '../lib/deskStatus'
import { isNextHere } from '../lib/nextHere'
import { serviceLabel } from '../lib/plan'
import { offersPutBack, putBackQuestion } from '../lib/putBack'
import { formatMoment } from '../lib/staffText'
import { mins } from '../ui/hallParts'
import type { Colleague } from '../lib/transfer'
import { transferredFromName } from '../lib/transferredFrom'
import type { AwayView, CounterView, Permission, Service, Ticket, WorkProfile } from '../lib/types'

/** "X-001" drawn as a stub with X on it and 001 beside it. A number that does not
 *  start with its prefix is shown whole, with no stub. */
export function splitNumber(t: Pick<Ticket, 'displayNumber' | 'prefix'>): { letter: string; digits: string } {
  const p = t.prefix ?? ''
  if (p && t.displayNumber.startsWith(p)) {
    return { letter: p, digits: t.displayNumber.slice(p.length).replace(/^[-\s]/, '') }
  }
  return { letter: '', digits: t.displayNumber }
}

export function Num({ ticket, stub = 'stub--sm' }: { ticket: Pick<Ticket, 'displayNumber' | 'prefix'>; stub?: string }) {
  const { letter, digits } = splitNumber(ticket)
  return (
    <span className="counter-no" aria-label={ticket.displayNumber}>
      {letter && <span className={`stub ${stub}`} aria-hidden="true">{letter}</span>}
      <span aria-hidden="true">{digits}</span>
    </span>
  )
}

/** Who sent this customer here: a transfer icon and "Counter 2 · Anna"; the title says it whole. */
export function FromChip({ by }: { by: string }) {
  const { say } = useStaffText()
  return (
    <Chip size="sm" variant="soft" color="accent" className="cw-sent" title={by ? say('counter.transferredFrom', { by }) : say('counter.transferred')}>
      <Icon name="swap" size={12} />
      {by && <span className="sr-only">{say('counter.transferred')}</span>}
      <Chip.Label className="cw-sent__by">{by || say('counter.transferred')}</Chip.Label>
    </Chip>
  )
}

const serviceOf = (t: Ticket) => t.transactions[t.currentTransaction]?.serviceId ?? ''

/** A parked customer: `mine` when Call next could take them, `fetch` when Call may,
 *  `sent` when sent here or back to me (Transferred), `held` when this desk or person holds them. */
export type Pooled = { ticket: Ticket; mine: boolean; fetch: boolean; sent?: boolean; held?: boolean }

/** Beside the queues: Transferred (sent here or back to me, returns first) and
 *  On hold (this desk's own holds only; Call next never takes them). */
export function holdLists(pooled: readonly Pooled[]): { transferred: Pooled[]; onHold: Pooled[] } {
  const sent = pooled.filter((p) => p.sent)
  return {
    transferred: [...sent.filter((p) => p.ticket.returned), ...sent.filter((p) => !p.ticket.returned)],
    onHold: pooled.filter((p) => p.held && !p.sent),
  }
}

/** Customers I sent on with Send back to me who are not back yet; never one already in Transferred. */
export function comingBack(away: readonly AwayView[], transferred: readonly Pooled[]): AwayView[] {
  const here = new Set(transferred.map((p) => p.ticket.id))
  return away.filter((a) => !here.has(a.ticketId))
}

/** Coming back: one line under the tabs with a read-only list behind it, closed until asked. */
export function ComingBackLine({
  away,
  tickets,
  named,
}: {
  away: readonly AwayView[]
  tickets: readonly Ticket[]
  named: readonly Service[]
}) {
  const { lang, say } = useStaffText()
  const [open, setOpen] = useState(false)
  const listId = useId()
  const toggle = useRef<HTMLButtonElement>(null)
  // Leaving while focused (the last one came back): hand focus to the chosen tab, not <body>.
  // A layout cleanup runs before React removes the button, so it still holds focus here.
  useLayoutEffect(() => {
    const button = toggle.current
    return () => {
      if (button && document.activeElement === button) {
        button.closest('.cw-panel')?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')?.focus()
      }
    }
  }, [])
  return (
    <div className="cw-back">
      <button
        ref={toggle}
        type="button"
        className="cw-back__toggle"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((o) => !o)}
      >
        {say('counter.comingBack')}{' '}
        <span className="cw-back__count">{away.length}</span>
        <Icon name="chevron" size={16} />
      </button>
      <ul id={listId} className="cw-rows" hidden={!open}>
        {away.map((a) => (
          <li key={a.ticketId} className="cw-row">
            <Num ticket={tickets.find((t) => t.id === a.ticketId) ?? { displayNumber: a.displayNumber, prefix: '' }} />
            <span className="cw-row__what">
              <span className="cw-row__name">
                <span>{serviceLabel(named, a.serviceId, lang)}</span>
              </span>
              <span className="cw-row__sub">{awayStage(a.state) === 'serving' ? say('counter.away.serving') : say('counter.away.waiting')}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Everyone waiting at this desk in one list, longest wait first, whatever their service. */
export function byLongestWait(tickets: readonly Ticket[], now: number): Ticket[] {
  return tickets
    .map((t) => ({ t, w: callingWaitSeconds(t, now) }))
    .sort((a, b) => b.w - a.w || Date.parse(a.t.issuedAt) - Date.parse(b.t.issuedAt))
    .map(({ t }) => t)
}

/** An empty tab: an icon and a few words. */
function Nobody({ icon, title }: { icon: IconName; title: string }) {
  return (
    <EmptyState className="cw-nobody">
      <span className="cw-nobody__icon" aria-hidden="true">
        <Icon name={icon} size={18} />
      </span>
      <p>{title}</p>
    </EmptyState>
  )
}

/** A KPI tile: the figure large, its label under it. */
function Figure({ label, value, over }: { label: string; value: ReactNode; over?: boolean }) {
  return (
    <div className="cw-fig" data-over={over || undefined}>
      <b>{value}</b>
      <span>{label}</span>
    </div>
  )
}

/**
 * Waiting for you: three figures, then three tabs: everyone waiting (one list, longest
 * wait first), Transferred to me (sent to me or back to me) and On hold (my own holds);
 * while any are away, a Coming back line under them. A row's Call is drawn only where the server would take it.
 */
export function QueueInfo({
  callable,
  waiting,
  heldBack,
  pooled,
  away,
  outOfTurn,
  tickets,
  nextId,
  desk,
  deskProfile,
  occupied,
  mayCall,
  busy,
  counters,
  colleagues,
  named,
  permissions,
  zone,
  now,
  longest,
  breaching,
  ownReason,
  onCall,
  onPutBack,
}: {
  callable: Ticket[]
  /** Everybody Call next could take here (the line and those sent to me): the card's count too. */
  waiting: number
  heldBack: Ticket[]
  pooled: Pooled[]
  away: AwayView[]
  /** Call out of turn is on for this organisation. */
  outOfTurn: boolean
  tickets: Ticket[]
  nextId: string | undefined
  desk: CounterView
  deskProfile: WorkProfile | undefined
  occupied: boolean
  mayCall: boolean
  busy: boolean
  counters: CounterView[]
  colleagues: Colleague[]
  named: readonly Service[]
  permissions: readonly Permission[]
  zone: string | undefined
  now: number
  longest: number
  breaching: number
  /** Whether a park reason is one the screens write themselves, not a clerk's words. */
  ownReason: (reason: string) => boolean
  onCall: (t: Ticket) => void
  onPutBack: (t: Ticket) => Promise<boolean>
}) {
  const { lang, say } = useStaffText()
  const [tab, setTab] = useState<'queues' | 'sent' | 'hold'>('queues')
  const [puttingBack, setPuttingBack] = useState('')
  const { transferred, onHold } = holdLists(pooled)
  const returning = comingBack(away, transferred)
  const tag = (t: Ticket) => (t.priority === 'vip' ? say('common.vip') : t.priority !== 'standard' ? say('common.priority') : '')
  // A clerk's own hold reason, or '' for one the screens wrote.
  const reasonOf = (t: Ticket) => (t.parkReason && !ownReason(t.parkReason) ? t.parkReason : '')
  // Sent here by somebody: its own line under the row, so the sender's name fits.
  const sentLine = (t: Ticket) => {
    const by = transferredFromName(t, counters, colleagues)
    return by === null ? null : (
      <span className="cw-row__sent">
        <FromChip by={by} />
      </span>
    )
  }
  const nextChip = (
    <Chip size="sm" variant="primary" color="accent" className="counter-next">
      {say('counter.next')}
    </Chip>
  )

  const callButton = (t: Ticket, label: string) =>
    mayCall ? (
      <Button
        variant={t.id === nextId ? 'secondary' : 'tertiary'}
        size="sm"
        className="cw-rowbtn"
        isDisabled={busy}
        aria-label={callName(label, t.displayNumber)}
        onPress={() => onCall(t)}
      >
        {label}
      </Button>
    ) : (
      <span />
    )

  // A Transferred or On hold row: the hold's reason or the service, the chip, the time aside.
  const poolRow = ({ ticket, mine, fetch }: Pooled, call: string) => (
    <li key={ticket.id} className="cw-row">
      <Num ticket={ticket} />
      {puttingBack === ticket.id ? (
        <div className="cw-row__ask">
          <p>{putBackQuestion(ticket.displayNumber, lang)}</p>
          <div>
            <Button variant="ghost" size="sm" onPress={() => setPuttingBack('')}>{say('common.cancel')}</Button>
            <Button
              variant="secondary"
              size="sm"
              isDisabled={busy}
              onPress={() => void onPutBack(ticket).then((ok) => ok && setPuttingBack(''))}
            >
              {say('counter.putBack')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <span className="cw-row__what" data-reason={reasonOf(ticket) ? '' : undefined}>
            <span className="cw-row__name" title={reasonOf(ticket) || undefined}>
              <span>{reasonOf(ticket) || serviceLabel(named, serviceOf(ticket), lang)}</span>
            </span>
            {/* Next goes under the name here: these rows' wider button column leaves the name little room. */}
            <span className="cw-row__sub">
              {isNextHere(ticket, desk, occupied) && nextChip}
              {ticket.returned && <Chip size="sm" variant="soft">{say('counter.returned')}</Chip>}
              {isHeld(ticket, now) && <span>{say('counter.backInMin', { m: backInMinutes(ticket, now) })}</span>}
            </span>
          </span>
          <span className="cw-row__wait">{mins(parkedSeconds(ticket, now), say)}</span>
          {fetch ? (
            callButton(ticket, call)
          ) : offersPutBack(permissions, mine) ? (
            <Button variant="tertiary" size="sm" className="cw-rowbtn" isDisabled={busy} onPress={() => setPuttingBack(ticket.id)}>
              {say('counter.putBack')}
            </Button>
          ) : (
            <span />
          )}
          {!ticket.returned && sentLine(ticket)}
        </>
      )}
    </li>
  )

  const waitingRow = (t: Ticket) => {
    const mark = bookedMark(t, lang, zone)
    const from = mark ? callableFrom(t, deskProfile, now) : null
    const w = callingWaitSeconds(t, now)
    return (
      <li key={t.id} className="cw-row" data-next={t.id === nextId || undefined}>
        <Num ticket={t} />
        <span className="cw-row__what">
          <span className="cw-row__name">
            <span>{serviceLabel(named, serviceOf(t), lang)}</span>
            {isNextHere(t, desk, occupied) && nextChip}
          </span>
          <span className="cw-row__sub">
            {tag(t) && (
              <Chip size="sm" variant="soft" color="warning">
                {tag(t)}
              </Chip>
            )}
            {mark && (
              <Chip size="sm" variant="soft" title={from ? say('bookings.callableFrom', { time: formatMoment(from, lang, { time: true, zone }) }) : undefined}>
                {mark}
              </Chip>
            )}
          </span>
        </span>
        <span className="cw-row__wait" data-over={(t.serviceLevelSec && w >= t.serviceLevelSec) || undefined}>
          {mins(w, say)}
        </span>
        {t.id === nextId || (outOfTurn && offersOutOfTurnCall(t.id, nextId, permissions)) ? callButton(t, say('counter.call')) : <span />}
        {sentLine(t)}
      </li>
    )
  }

  const tabLabel = (label: string, n: number) => (
    <>
      <span className="cw-tab">{label}</span>
      <span className="cw-tabcount">{n}</span>
    </>
  )

  return (
    <Card className="cw-panel" role="region" aria-labelledby="cw-queue-title">
      <Card.Header className="cw-panel__head">
        <Card.Title id="cw-queue-title">{say('counter.waitingForYou')}</Card.Title>
        <Chip size="sm" variant="soft">{say('counter.nWaiting', { n: waiting })}</Chip>
      </Card.Header>
      <div className="cw-figs" data-over={breaching > 0 || undefined}>
        <Figure label={say('counter.stat.waiting')} value={waiting} />
        <Figure label={say('counter.stat.longest')} value={callable.length > 0 ? mins(longest, say) : '—'} />
        <Figure label={say('counter.stat.pastTarget')} value={breaching} over={breaching > 0} />
      </div>
      <Tabs className="cw-tabs" variant="secondary" selectedKey={tab} onSelectionChange={(k) => setTab(k as typeof tab)}>
        <Tabs.ListContainer>
          <Tabs.List aria-label={say('counter.waitingForYou')}>
            <Tabs.Tab id="queues">
              {tabLabel(say('counter.queues'), callable.length)}
              <Tabs.Indicator />
            </Tabs.Tab>
            <Tabs.Tab id="sent">
              {tabLabel(say('counter.transferredToMe'), transferred.length)}
              <Tabs.Indicator />
            </Tabs.Tab>
            <Tabs.Tab id="hold">
              {tabLabel(say('counter.setAside'), onHold.length)}
              <Tabs.Indicator />
            </Tabs.Tab>
          </Tabs.List>
        </Tabs.ListContainer>
        <Tabs.Panel id="queues" className="cw-groups">
          {callable.length === 0 ? (
            <Nobody icon="clock" title={say('counter.nobodyWaiting')} />
          ) : (
            <ul className="cw-rows">{byLongestWait(callable, now).map(waitingRow)}</ul>
          )}
          {heldBack.length > 0 && (
            <p className="cw-foot">
              {say('counter.heldBack', {
                list: heldBack.map((t) => say('counter.heldBackItem', { number: t.displayNumber, m: backInMinutes(t, now) })).join(', '),
              })}
            </p>
          )}
        </Tabs.Panel>
        <Tabs.Panel id="sent" className="cw-groups">
          {transferred.length === 0 && <Nobody icon="swap" title={say('counter.noneTransferred')} />}
          {transferred.length > 0 && <ul className="cw-rows cw-rows--wide">{transferred.slice(0, 12).map((p) => poolRow(p, say('counter.call')))}</ul>}
        </Tabs.Panel>
        <Tabs.Panel id="hold" className="cw-groups">
          {onHold.length === 0 && <Nobody icon="pause" title={say('counter.noneOnHold')} />}
          {onHold.length > 0 && <ul className="cw-rows cw-rows--wide">{onHold.slice(0, 12).map((p) => poolRow(p, say('counter.callAgain')))}</ul>}
        </Tabs.Panel>
      </Tabs>
      {returning.length > 0 && <ComingBackLine away={returning} tickets={tickets} named={named} />}
    </Card>
  )
}

/** A row button's name: its visible words first, so "Call again" can be spoken to it. */
export function callName(label: string, number: string): string {
  return label + ' ' + number
}
